import { useMemo, useState, useEffect, RefObject } from 'react';
import { connect } from 'react-redux';

import { HEADER_STICKED_HEIGHT } from 'CorePackage/HeaderModule/constants/headerStickedHeight';
import { Sidebar } from 'CorePackage/MainModule/components/Sidebar';
import { ROUTE_PM, ROUTE_PM_FROM_ANALYTICS__ID } from 'CorePackage/MainModule/routes/routes';
import {
  getKnmDataLateChangesFields,
  getKnmPath,
  getKnmDataCreated,
  getKnmUrlId,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';

import { basicPmMenuItems } from '../constants/basicPmMenuItems';
import { PmMenuItemsIdsEnum } from '../enums/PmMenuItemsIdsEnum';
import { IKnmPmMenuItem } from '../interfaces/IKnmPmMenuItem';
import { IKnmHistoryItem } from '../../../APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { getLateFiledLinkedName } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/common/getLateFiledLinkedName';

interface IAnchor {
  route: string;
  currentAnchorElement: HTMLElement | null;
  nextAnchorElement: HTMLElement | null;
}

interface IKnmPmMenuProps {
  dataRef: RefObject<HTMLDivElement>;
  hiddenItemsIds?: PmMenuItemsIdsEnum[];
  lateChangesFieldLinks: Map<string, string>;
}

interface IKnmPmMenuStoreProps {
  knmUrlId: string | undefined;
  lateChangesFields: IKnmHistoryItem[] | null | undefined;
  path: string | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
}

// Duplicated, after clarification need implementation of common MenuModule
const KnmPmMenuWithoutStore = ({
  dataRef,
  hiddenItemsIds,
  knmUrlId,
  lateChangesFields,
  lateChangesFieldLinks,
  path,
  created,
  settings,
}: IKnmPmMenuProps & IKnmPmMenuStoreProps) => {
  const [locationHash, setLocationHash] = useState(window.location.hash);

  const menuItemWarning = (menuItem: IKnmPmMenuItem) => {
    const resultOfInspectionLateChangesPath = '/resultOfInspection';
    const violationsLateChangesPath = '/isViolations';
    const pmResultDecisionsLateChangesPath = '/pmResultDecisions';
    const subjectLateChangesPath = '/subject';
    const inspectorsLateChangesPath = '/inspectors';
    const objectsLateChangesPath = '/objects';

    if (!lateChangesFields) {
      return menuItem.warning;
    }

    const lateChanges = lateChangesFields.flatMap(x => x.changes);

    if (
      menuItem.id === PmMenuItemsIdsEnum.PM_RESULT_OF_INSPECTION &&
      lateChanges?.find(
        x =>
          x?.field.includes(resultOfInspectionLateChangesPath) ||
          x?.field.includes(violationsLateChangesPath) ||
          x?.field.includes(pmResultDecisionsLateChangesPath),
      )
    ) {
      return (
        <>
          {lateChanges?.find(x => x?.field.includes(resultOfInspectionLateChangesPath)) &&
            getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, resultOfInspectionLateChangesPath)}
          {lateChanges?.find(x => x?.field.includes(violationsLateChangesPath)) &&
            getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, violationsLateChangesPath)}
          {lateChanges?.find(x => x?.field.includes(pmResultDecisionsLateChangesPath)) &&
            getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, pmResultDecisionsLateChangesPath)}
        </>
      );
    }

    if (
      menuItem.id === PmMenuItemsIdsEnum.PM_OBJECTS &&
      lateChanges?.find(x => x?.field.includes(objectsLateChangesPath))
    ) {
      return getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, objectsLateChangesPath);
    }

    if (
      menuItem.id === PmMenuItemsIdsEnum.PM_SUBJECT &&
      lateChanges?.find(x => x?.field.includes(subjectLateChangesPath))
    ) {
      return getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, subjectLateChangesPath);
    }

    if (
      menuItem.id === PmMenuItemsIdsEnum.PM_INSPECTORS &&
      lateChanges?.find(x => x?.field.includes(inspectorsLateChangesPath))
    ) {
      return getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, inspectorsLateChangesPath);
    }

    return menuItem.warning;
  };

  const menuItems = useMemo(
    () =>
      basicPmMenuItems(created, settings).reduce((newMenuItems: IKnmPmMenuItem[], basicMenuItem) => {
        if (hiddenItemsIds?.some(hiddenItemId => hiddenItemId === basicMenuItem.id)) {
          return newMenuItems;
        }

        if (lateChangesFields && lateChangesFieldLinks.size > 0) {
          newMenuItems.push({
            id: basicMenuItem.id,
            route: basicMenuItem.route,
            title: basicMenuItem.title,
            warning: menuItemWarning(basicMenuItem),
          });
        } else {
          newMenuItems.push(basicMenuItem);
        }

        return newMenuItems;
      }, []),
    [hiddenItemsIds, lateChangesFields, lateChangesFieldLinks.size],
  );

  const [anchors, setAnchors] = useState<IAnchor[]>([]);

  useEffect(() => {
    setAnchors(
      menuItems.map((item, i) => {
        const isLast = menuItems.length === i + 1;
        const anchorId = item.route.replace('#', '');
        const currentAnchorElement = document.getElementById(anchorId);
        const nextAnchorId = !isLast ? menuItems[i + 1].route.replace('#', '') : null;
        const nextAnchorElement = nextAnchorId ? document.getElementById(nextAnchorId) : null;

        return {
          route: item.route,
          currentAnchorElement,
          nextAnchorElement,
        };
      }),
    );
  }, [menuItems]);

  useEffect(() => {
    const updateLocationHash = () => {
      const refOffset = dataRef.current?.getBoundingClientRect().top;

      if (refOffset || refOffset === 0) {
        if (refOffset < HEADER_STICKED_HEIGHT) {
          const isPageBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;

          if (isPageBottom && anchors.length > 0) {
            const lastRoute = anchors[anchors.length - 1].route;

            if (window.location.hash !== lastRoute) {
              window.history.replaceState(null, '', lastRoute);
            }
          } else {
            anchors.forEach(({ route, currentAnchorElement, nextAnchorElement }) => {
              const currentAnchorOffsetTop = currentAnchorElement?.getBoundingClientRect().top!;
              const nextAnchorOffsetTop = nextAnchorElement?.getBoundingClientRect().top!;

              if (
                currentAnchorOffsetTop <= HEADER_STICKED_HEIGHT &&
                nextAnchorOffsetTop > HEADER_STICKED_HEIGHT &&
                window.location.hash !== route
              ) {
                window.history.replaceState(null, '', route);
              }
            });
          }
        } else if (window.location.hash && knmUrlId) {
          if (path?.includes('/analytics/')) {
            window.history.replaceState(null, '', ROUTE_PM_FROM_ANALYTICS__ID(knmUrlId));
          } else {
            window.history.replaceState(null, '', ROUTE_PM(knmUrlId));
          }
        }
      }
      setLocationHash(window.location.hash);
    };

    updateLocationHash();

    window.addEventListener('scroll', updateLocationHash);

    return () => window.removeEventListener('scroll', updateLocationHash);
  }, [anchors, knmUrlId, dataRef]);

  return (
    <Sidebar
      type="knm-menu"
      menuItems={menuItems}
      locationHash={locationHash}
      itemsIsAnchors
      useClickableWarning={true}
    />
  );
};

const mapStateToProps = (state: IRootState): IKnmPmMenuStoreProps => ({
  knmUrlId: getKnmUrlId(state),
  path: getKnmPath(state),
  lateChangesFields: getKnmDataLateChangesFields(state),
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
});

export const KnmPmMenu = connect(mapStateToProps)(KnmPmMenuWithoutStore);
